/**
 * ThrottlingForecastChart.tsx
 * Feature: 154 - Eventstream Real-Time Capacity Telemetry
 * Author: Peter Dobler
 * Date: 2026-05-18
 * Constitution Version: 1.12.0
 *
 * Three-horizon throttling risk display (10, 30, 60 minutes).
 * Shows projected CU%, risk %, and estimated throttle time when applicable.
 */

import React from 'react';
import { AlertTriangle } from 'lucide-react';
import { Alert, Card, Spinner, AppTooltip } from '../ui';
import type { ThrottlingForecast, ForecastHorizon } from '@shared/types/capacityTelemetry';
import { formatEasternTime } from '@/utils/dateFormatter';

// ---------------------------------------------------------------------------
// Risk color
// ---------------------------------------------------------------------------

type RiskColor = 'success' | 'warning' | 'error';

function riskColor(pct: number): RiskColor {
  if (pct < 30) return 'success';
  if (pct < 70) return 'warning';
  return 'error';
}

function riskLabel(pct: number): string {
  if (pct < 30) return 'Low';
  if (pct < 70) return 'Moderate';
  return 'High';
}

const RISK_TOKEN: Record<RiskColor, string> = {
  success: 'var(--color-status-success)',
  warning: 'var(--color-status-warning)',
  error: 'var(--color-status-danger)',
};

// ---------------------------------------------------------------------------
// Horizon card
// ---------------------------------------------------------------------------

function HorizonCard({ horizon }: { horizon: ForecastHorizon }) {
  const color = riskColor(horizon.throttlingRiskPct);
  const label = riskLabel(horizon.throttlingRiskPct);
  const accent = RISK_TOKEN[color];

  return (
    <Card
      variant="outlined"
      padding="none"
      className="flex-1 p-4"
      style={
        color === 'error' || color === 'warning' ? { borderColor: accent } : undefined
      }
    >
      <span className="text-xs font-semibold text-[var(--color-text-secondary)]">
        +{horizon.minutes} min
      </span>

      <div className="mb-0.5 mt-2">
        <div className="flex justify-between">
          <span className="text-xs text-[var(--color-text-secondary)]">Throttle risk</span>
          <span className="text-xs font-bold" style={{ color: accent }}>
            {label} ({horizon.throttlingRiskPct.toFixed(0)}%)
          </span>
        </div>
        <div className="mt-0.5 h-1.5 w-full overflow-hidden rounded-[3px] bg-[var(--color-bg-subtle)]">
          <div
            className="h-full"
            style={{ width: `${horizon.throttlingRiskPct}%`, backgroundColor: accent }}
          />
        </div>
      </div>

      {horizon.projectedCuPct !== null && (
        <span className="text-xs text-[var(--color-text-secondary)]">
          Projected CU: <strong>{horizon.projectedCuPct.toFixed(1)}%</strong>
        </span>
      )}

      {horizon.willThrottle && horizon.estimatedThrottleAt && (
        <div className="mt-0.5 flex items-center gap-0.5">
          <AlertTriangle className="h-3.5 w-3.5 text-[var(--color-status-warning)]" />
          <span className="text-xs text-[var(--color-status-warning)]">
            Throttle projected at{' '}
            {formatEasternTime(horizon.estimatedThrottleAt)}
          </span>
        </div>
      )}
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

interface ThrottlingForecastChartProps {
  forecast: ThrottlingForecast | undefined;
  loading: boolean;
  error?: Error | null;
}

export default function ThrottlingForecastChart({
  forecast,
  loading,
  error,
}: ThrottlingForecastChartProps) {
  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <Spinner size="md" />
      </div>
    );
  }

  if (error) {
    return <Alert variant="error">Failed to load forecast data.</Alert>;
  }

  if (!forecast) {
    return null;
  }

  if (forecast.insufficientData) {
    return (
      <Alert variant="info">
        Insufficient data — need at least 20 minutes of telemetry history to generate a forecast.
        ({forecast.dataPointsUsed} / 40 data points collected)
      </Alert>
    );
  }

  return (
    <div>
      <div className="mb-1.5 flex justify-between">
        <span className="text-sm font-medium text-[var(--color-text-secondary)]">
          Throttling Risk Forecast
        </span>
        <AppTooltip
          title={`Based on ${forecast.dataPointsUsed} data points · Generated ${formatEasternTime(forecast.generatedAt)}`}
        >
          <span className="cursor-help text-xs text-[var(--color-text-secondary)]">
            {forecast.dataPointsUsed} pts · linear regression
          </span>
        </AppTooltip>
      </div>

      <div className="flex gap-3">
        {forecast.horizons.map((h) => (
          <HorizonCard key={h.minutes} horizon={h} />
        ))}
      </div>
    </div>
  );
}
